import { supabase } from '../lib/supabase';
import { Medicoes, MedicoesAgregado, StatusMedicao } from '../types/medicao';
import { getAgregadosMotoristasList } from './pessoaService';

export function cleanStatusMedicaoName(name: string): string {
  if (!name) return 'Em Análise';
  let cleaned = name
    .replace(/An\?\?lise/gi, 'Análise')
    .replace(/An\?lise/gi, 'Análise')
    .replace(/Em An\?\?lise/gi, 'Em Análise')
    .replace(/Em Analise/gi, 'Em Análise')
    .replace(/Aprova\?\?o/gi, 'Aprovação')
    .replace(/Aprova\?o/gi, 'Aprovação')
    .replace(/Aprovad[oa]\?/gi, 'Aprovado')
    .replace(/N\?o/gi, 'Não')
    .replace(/Conclu\?do/gi, 'Concluído')
    .replace(/Cancelad[oa]\?/gi, 'Cancelado')
    .trim();

  if (cleaned.toLowerCase() === 'analise' || cleaned.toLowerCase() === 'em analise' || cleaned.toLowerCase().includes('an')) {
    cleaned = 'Em Análise';
  }
  return cleaned;
}

export const medicaoService = {
  /**
   * Auto-reconciles any orphaned OSs (OS with medicoes_id / medicoes_agregado that lacks a parent header).
   * Automatically reconstructs or restores missing medicao headers so that no measurement can disappear.
   */
  async reconcileOrphanedMedicoes(): Promise<{ restoredClientes: number; restoredAgregados: number }> {
    let restoredClientes = 0;
    let restoredAgregados = 0;

    try {
      // 1. Check for orphaned cliente OSs (medicoes_id > 1 without header in medicoes)
      const { data: clientOS } = await supabase
        .from('ordem_de_servico')
        .select('id, data_atendimento, total_os, valor_unit_servi, medicoes_id, pedido_id')
        .not('medicoes_id', 'is', null)
        .gt('medicoes_id', 1);

      if (clientOS && clientOS.length > 0) {
        const uniqueMedIds = Array.from(
          new Set(clientOS.map(o => Number(o.medicoes_id)).filter(id => !isNaN(id) && id > 1))
        );

        if (uniqueMedIds.length > 0) {
          const { data: existingMedicoes } = await supabase
            .from('medicoes')
            .select('id')
            .in('id', uniqueMedIds);

          const existingIdsSet = new Set((existingMedicoes || []).map(m => m.id));
          const missingMedIds = uniqueMedIds.filter(id => !existingIdsSet.has(id));

          for (const mId of missingMedIds) {
            const osForThisMed = clientOS.filter(o => Number(o.medicoes_id) === mId);
            if (osForThisMed.length > 0) {
              let minDate = osForThisMed[0].data_atendimento || '';
              let maxDate = osForThisMed[0].data_atendimento || '';
              let totalVal = 0;

              for (const os of osForThisMed) {
                if (os.data_atendimento) {
                  if (!minDate || os.data_atendimento < minDate) minDate = os.data_atendimento;
                  if (!maxDate || os.data_atendimento > maxDate) maxDate = os.data_atendimento;
                }
                totalVal += Number(os.total_os || os.valor_unit_servi || 0);
              }

              const primaryOS = osForThisMed[0];

              const { error: insErr } = await supabase
                .from('medicoes')
                .insert([
                  {
                    id: mId,
                    ordem_de_servico_id: primaryOS.id,
                    status_medicao_id: 1, // Em Análise
                    periodo_inicial: minDate || new Date().toISOString().split('T')[0],
                    periodo_final: maxDate || new Date().toISOString().split('T')[0],
                    valor_medicao: totalVal
                  }
                ]);

              if (!insErr) {
                restoredClientes++;
                console.log(`[AutoHeal] Restored missing client medicao #${mId} (${osForThisMed.length} OSs, R$ ${totalVal})`);
              }
            }
          }
        }
      }

      // 2. Check for orphaned agregado OSs (medicoes_agregado > 1 without header in medicoes_agregado)
      const { data: agregadoOS } = await supabase
        .from('ordem_de_servico')
        // O condutor da OS é a coluna `agregado`; `motorista_id` não existe aqui
        // e derrubava a consulta inteira (42703).
        .select('id, data_atendimento, agregado, valor_total_repasses, repasse, medicoes_agregado')
        .not('medicoes_agregado', 'is', null)
        .gt('medicoes_agregado', 1);

      if (agregadoOS && agregadoOS.length > 0) {
        const uniqueAgregadoMedIds = Array.from(
          new Set(agregadoOS.map(o => Number(o.medicoes_agregado)).filter(id => !isNaN(id) && id > 1))
        );

        if (uniqueAgregadoMedIds.length > 0) {
          const { data: existingAgregadoMedicoes } = await supabase
            .from('medicoes_agregado')
            .select('id')
            .in('id', uniqueAgregadoMedIds);

          const existingAgregadoIdsSet = new Set((existingAgregadoMedicoes || []).map(m => m.id));
          const missingAgregadoMedIds = uniqueAgregadoMedIds.filter(id => !existingAgregadoIdsSet.has(id));

          for (const mId of missingAgregadoMedIds) {
            const osForThisMed = agregadoOS.filter(o => Number(o.medicoes_agregado) === mId);
            if (osForThisMed.length > 0) {
              let minDate = osForThisMed[0].data_atendimento || '';
              let maxDate = osForThisMed[0].data_atendimento || '';
              let totalRepasse = 0;
              let agregadoId = osForThisMed[0].agregado || null;

              for (const os of osForThisMed) {
                if (os.data_atendimento) {
                  if (!minDate || os.data_atendimento < minDate) minDate = os.data_atendimento;
                  if (!maxDate || os.data_atendimento > maxDate) maxDate = os.data_atendimento;
                }
                totalRepasse += Number(os.valor_total_repasses || os.repasse || 0);
                if (!agregadoId && os.agregado) {
                  agregadoId = os.agregado;
                }
              }

              if (agregadoId) {
                const { error: insErr } = await supabase
                  .from('medicoes_agregado')
                  .insert([
                    {
                      id: mId,
                      agregado_id: agregadoId,
                      status_medicao_id: 1, // Em Análise
                      periodo_inicial: minDate || new Date().toISOString().split('T')[0],
                      periodo_final: maxDate || new Date().toISOString().split('T')[0],
                      valor_medicao: totalRepasse
                    }
                  ]);

                if (!insErr) {
                  restoredAgregados++;
                  console.log(`[AutoHeal] Restored missing agregado medicao #${mId} (${osForThisMed.length} OSs, R$ ${totalRepasse})`);
                }
              }
            }
          }
        }
      }
    } catch (err) {
      console.warn('[AutoHeal] Error checking orphaned medicoes:', err);
    }

    return { restoredClientes, restoredAgregados };
  },

  async getMedicoes(page = 1, limit = 50): Promise<{ data: Medicoes[], count: number }> {
    // Proactive self-healing check on first page load
    if (page === 1) {
      try {
        await medicaoService.reconcileOrphanedMedicoes();
      } catch (e) {
        console.warn('[getMedicoes] Reconcile attempt non-blocking error:', e);
      }
    }

    const from = (page - 1) * limit;
    const to = from + limit - 1;

    let { data, error, count } = await supabase
      .from('medicoes')
      .select('*', { count: 'exact' })
      .order('id', { ascending: false })
      .range(from, to);

    if (error) {
      console.warn('[getMedicoes] Error fetching basic query:', error.message);
      const retry = await supabase
        .from('medicoes')
        .select('*', { count: 'exact' })
        .range(from, to);
      data = retry.data;
      error = retry.error;
      count = retry.count;
    }

    if (error || !data) {
      console.error('Error fetching medicoes:', error);
      return { data: [], count: 0 };
    }

    const medicaoList: Medicoes[] = data || [];
    const medicaoIds = medicaoList.map(m => Number(m.id)).filter(id => !isNaN(id) && id > 0);

    if (medicaoIds.length === 0) {
      return { data: [], count: count || 0 };
    }

    // Map for storing resolved clients
    const clientMap = new Map<number, { id: number; nome: string }>();

    // Step 0: Highest Priority - Read ordem_de_servico_id as Pessoa ID!
    // The application actively saves the selected Cliente ID into the ordem_de_servico_id column.
    const directPessoaIds = Array.from(new Set(medicaoList.map(m => m.ordem_de_servico_id).filter(Boolean))) as number[];
    if (directPessoaIds.length > 0) {
      try {
        const { data: pessoaList, error: pessoaErr } = await supabase
          .from('pessoa')
          .select('id, nome')
          .in('id', directPessoaIds);
          
        if (!pessoaErr && pessoaList && pessoaList.length > 0) {
          const pMap = new Map<number, { id: number; nome: string }>();
          for (const p of pessoaList) {
            pMap.set(p.id, { id: p.id, nome: String(p.nome).trim() });
          }
          
          for (const m of medicaoList) {
            if (m.ordem_de_servico_id && pMap.has(m.ordem_de_servico_id)) {
              clientMap.set(m.id, pMap.get(m.ordem_de_servico_id)!);
            }
          }
        }
      } catch (e) {
        console.warn('[getMedicoes] Step 0 error:', e);
      }
    }

    // Step 1: Direct query on ordem_de_servico by medicoes_id (for unmapped only)
    const unmappedForStep1 = medicaoIds.filter(id => !clientMap.has(id));
    if (unmappedForStep1.length > 0) {
      try {
        const { data: osList, error: osErr } = await supabase
          .from('ordem_de_servico')
          .select('id, medicoes_id, pedido_id, pedido:pedido_id(pessoa_id, pessoa:pessoa_id(id, nome))')
          .in('medicoes_id', unmappedForStep1);

      if (!osErr && osList && osList.length > 0) {
        for (const os of osList) {
          const medId = Number(os.medicoes_id);
          if (medId && !clientMap.has(medId)) {
            const rawP = (os as any).pedido?.pessoa;
            const pObj = Array.isArray(rawP) ? rawP[0] : rawP;
            const pId = pObj?.id || (os as any).pedido?.pessoa_id;
            const pNome = pObj?.nome;
            if (pNome) {
              clientMap.set(medId, { id: pId, nome: String(pNome).trim() });
            }
          }
        }
      }
    } catch (e) {
      console.warn('[getMedicoes] Step 1 error:', e);
    }
    }

    // Step 2: Separate 2-step lookup for any still unmapped medicoes
    const unmappedAfterStep1 = medicaoIds.filter(id => !clientMap.has(id));
    if (unmappedAfterStep1.length > 0) {
      try {
        const { data: rawOS } = await supabase
          .from('ordem_de_servico')
          .select('id, medicoes_id, pedido_id')
          .in('medicoes_id', unmappedAfterStep1);

        if (rawOS && rawOS.length > 0) {
          const pedidoIds = Array.from(new Set(rawOS.map(o => o.pedido_id).filter(Boolean))) as number[];
          if (pedidoIds.length > 0) {
            const { data: pedidos } = await supabase
              .from('pedido')
              .select('id, pessoa_id, pessoa:pessoa_id(id, nome)')
              .in('id', pedidoIds);

            const pedMap = new Map<number, { id: number; nome: string }>();
            if (pedidos) {
              for (const ped of pedidos) {
                const rawP = (ped as any).pessoa;
                const p = Array.isArray(rawP) ? rawP[0] : rawP;
                if (p?.nome) {
                  pedMap.set(ped.id, { id: p.id || ped.pessoa_id, nome: String(p.nome).trim() });
                } else if (ped.pessoa_id) {
                  pedMap.set(ped.id, { id: ped.pessoa_id, nome: '' });
                }
              }
            }

            const missingPessoaIds = Array.from(pedMap.values()).filter(v => !v.nome).map(v => v.id);
            if (missingPessoaIds.length > 0) {
              const { data: pessoas } = await supabase.from('pessoa').select('id, nome').in('id', missingPessoaIds);
              if (pessoas) {
                const pNameMap = new Map(pessoas.map(p => [p.id, p.nome.trim()]));
                for (const [pId, pInfo] of pedMap.entries()) {
                  if (!pInfo.nome && pNameMap.has(pInfo.id)) {
                    pedMap.set(pId, { id: pInfo.id, nome: pNameMap.get(pInfo.id)! });
                  }
                }
              }
            }

            for (const os of rawOS) {
              const medId = Number(os.medicoes_id);
              if (medId && !clientMap.has(medId) && os.pedido_id && pedMap.has(os.pedido_id)) {
                const client = pedMap.get(os.pedido_id)!;
                if (client.nome) {
                  clientMap.set(medId, client);
                }
              }
            }
          }
        }
      } catch (e) {
        console.warn('[getMedicoes] Step 2 error:', e);
      }
    }

    // Step 3: Check medicoes_detalhes for any still unmapped medicoes
    const unmappedAfterStep2 = medicaoIds.filter(id => !clientMap.has(id));
    if (unmappedAfterStep2.length > 0) {
      try {
        const { data: detList } = await supabase
          .from('medicoes_detalhes')
          .select('medicao_id, ordem_de_servico_id')
          .in('medicao_id', unmappedAfterStep2);

        if (detList && detList.length > 0) {
          const osIds = Array.from(new Set(detList.map(d => d.ordem_de_servico_id).filter(Boolean))) as number[];
          if (osIds.length > 0) {
            const { data: detOSList } = await supabase
              .from('ordem_de_servico')
              .select('id, pedido_id, pedido:pedido_id(pessoa:pessoa_id(id, nome))')
              .in('id', osIds);

            if (detOSList) {
              const osToClient = new Map<number, { id: number; nome: string }>();
              for (const os of detOSList) {
                const rawP = (os as any).pedido?.pessoa;
                const pObj = Array.isArray(rawP) ? rawP[0] : rawP;
                if (pObj?.nome) {
                  osToClient.set(os.id, { id: pObj.id || (os as any).pedido?.pessoa_id, nome: String(pObj.nome).trim() });
                }
              }

              for (const det of detList) {
                const mId = Number(det.medicao_id);
                if (mId && !clientMap.has(mId) && det.ordem_de_servico_id && osToClient.has(det.ordem_de_servico_id)) {
                  clientMap.set(mId, osToClient.get(det.ordem_de_servico_id)!);
                }
              }
            }
          }
        }
      } catch (e) {
        console.warn('[getMedicoes] Step 3 error:', e);
      }
    }

    // Step 4: Fallback for empty medicoes that have an explicit cliente_id column
    const completelyUnmapped = medicaoList.filter(m => !clientMap.has(m.id));
    if (completelyUnmapped.length > 0) {
      const explicitClientIds = Array.from(new Set(
        completelyUnmapped.map(m => (m as any).cliente_id).filter(Boolean) as number[]
      ));
      if (explicitClientIds.length > 0) {
        try {
          const { data: cPessoas } = await supabase.from('pessoa').select('id, nome').in('id', explicitClientIds);
          if (cPessoas) {
            const cMap = new Map(cPessoas.map(p => [p.id, p.nome.trim()]));
            for (const m of completelyUnmapped) {
              const cId = (m as any).cliente_id;
              if (cId && cMap.has(cId)) {
                clientMap.set(m.id, { id: cId, nome: cMap.get(cId)! });
              }
            }
          }
        } catch (e) {
          console.warn('[getMedicoes] Step 4 error:', e);
        }
      }
    }

    // Map clients to medicoes list
    const enrichedData = medicaoList.map(m => {
      const client = clientMap.get(m.id);
      return {
        ...m,
        cliente_id: client?.id || (m as any).cliente_id || null,
        cliente_nome: client?.nome || '-',
        pedido: client ? { pessoa: { id: client.id, nome: client.nome } } : null
      };
    });

    return { data: enrichedData, count: count || 0 };
  },

  async getMedicoesAgregado(page = 1, limit = 50): Promise<{ data: MedicoesAgregado[], count: number }> {
    const from = (page - 1) * limit;
    const to = from + limit - 1;

    let { data, error, count } = await supabase
      .from('medicoes_agregado')
      .select('*, agregado:agregado_id(nome)', { count: 'exact' })
      .order('id', { ascending: false })
      .range(from, to);

    if (error) {
      console.warn('[getMedicoesAgregado] Error with join, retrying simpler query:', error.message);
      const retry = await supabase
        .from('medicoes_agregado')
        .select('*', { count: 'exact' })
        .order('id', { ascending: false })
        .range(from, to);
      data = retry.data;
      error = retry.error;
      count = retry.count;
    }

    if (error) {
      console.warn('[getMedicoesAgregado] Error with order, retrying basic query:', error.message);
      const retry2 = await supabase
        .from('medicoes_agregado')
        .select('*', { count: 'exact' })
        .range(from, to);
      data = retry2.data;
      error = retry2.error;
      count = retry2.count;
    }

    if (error) {
      console.error('Error fetching medicoes_agregado:', error);
      return { data: [], count: 0 };
    }
    
    return {
      data: (data || []).map(m => ({
        ...m,
        agregado_nome: (m as any).agregado?.nome
      })),
      count: count || 0
    };
  },

  async getClientesMedicao(): Promise<{ id: number; nome: string }[]> {
    try {
      const clientMap = new Map<number, string>();

      // 1. Check grupo_pessoa for 'cliente'
      const { data: grupoCliente } = await supabase
        .from('grupo_pessoa')
        .select('id')
        .ilike('nome', '%cliente%');

      if (grupoCliente && grupoCliente.length > 0) {
        const grupoIds = grupoCliente.map(g => g.id);
        const { data: pessoaGrupos } = await supabase
          .from('pessoa_grupo')
          .select('pessoa:pessoa_id(id, nome, ativo, data_desativacao)')
          .in('grupo_pessoa_id', grupoIds);

        if (pessoaGrupos) {
          for (const pg of pessoaGrupos) {
            const p: any = pg.pessoa;
            if (p && p.id && p.nome && (p.ativo !== false && !p.data_desativacao)) {
              clientMap.set(p.id, p.nome.trim());
            }
          }
        }
      }

      // 2. Add clients from pedidos (every entity that makes pedidos is a client)
      const { data: pedidos } = await supabase
        .from('pedido')
        .select('pessoa:pessoa_id(id, nome, ativo, data_desativacao)')
        .limit(200);

      if (pedidos) {
        for (const ped of pedidos) {
          const p: any = ped.pessoa;
          if (p && p.id && p.nome && (p.ativo !== false && !p.data_desativacao)) {
            clientMap.set(p.id, p.nome.trim());
          }
        }
      }

      // 3. If we found mapped clients, return them sorted
      if (clientMap.size > 0) {
        return Array.from(clientMap.entries())
          .map(([id, nome]) => ({ id, nome }))
          .sort((a, b) => a.nome.localeCompare(b.nome));
      }

      // 4. Fallback: query active pessoas excluding motoristas/agregados
      const { data: grupoNaoClientes } = await supabase
        .from('grupo_pessoa')
        .select('id')
        .or('nome.ilike.%motorista%,nome.ilike.%agregado%,nome.ilike.%fornecedor%');

      let excludeIds: number[] = [];
      if (grupoNaoClientes && grupoNaoClientes.length > 0) {
        const { data: exclPessoas } = await supabase
          .from('pessoa_grupo')
          .select('pessoa_id')
          .in('grupo_pessoa_id', grupoNaoClientes.map(g => g.id));
        if (exclPessoas) {
          excludeIds = exclPessoas.map(e => e.pessoa_id);
        }
      }

      let query = supabase.from('pessoa').select('id, nome, ativo, data_desativacao').order('nome');
      const { data: allPessoas, error } = await query;
      if (error || !allPessoas) return [];

      return allPessoas
        .filter(p => p.id && p.nome && (p.ativo !== false && !p.data_desativacao) && !excludeIds.includes(p.id))
        .map(p => ({ id: p.id, nome: p.nome.trim() }));
    } catch (e) {
      console.warn('[getClientesMedicao] Fallback due to error:', e);
      const { data } = await supabase.from('pessoa').select('id, nome').order('nome');
      return (data || []).map(p => ({ id: p.id, nome: p.nome }));
    }
  },

  async getAgregadosMedicao(): Promise<{ id: number; nome: string }[]> {
    return getAgregadosMotoristasList();
  },

  async getUnlinkedOSForMedicaoCliente(clienteId: number, inicio: string, fim: string, currentMedicaoId?: number): Promise<{ ordens: any[], total: number, count: number }> {
    try {
      if (!clienteId || !inicio || !fim) {
        return { ordens: [], total: 0, count: 0 };
      }

      // 1. Get all pedido IDs for this cliente
      const { data: pedidos } = await supabase
        .from('pedido')
        .select('id')
        .eq('pessoa_id', clienteId);

      const pedidoIds = pedidos ? pedidos.map(p => p.id) : [];
      const allTargetIds = Array.from(new Set([...pedidoIds, clienteId]));

      if (allTargetIds.length === 0) {
        return { ordens: [], total: 0, count: 0 };
      }

      // Build date bounds: ensure entire end date is included (inclusive up to 23:59:59)
      const startDateStr = inicio.split('T')[0];
      const endDateStr = fim.split('T')[0];

      // 2. Fetch OS for these pedidos
      const { data: osData, error } = await supabase
        .from('ordem_de_servico')
        .select('*, pedido:pedido_id(pessoa_id, pessoa:pessoa_id(id, nome)), veiculos:veiculos_id(placa, veiculo_modelo:veiculo_modelo_id(id, modelo), motorista:motorista_id(nome)), item_servico_rel:item_servico(id, servicos, tipo_servico:tipo_servico_id(id, tipo)), tipo_veiculo:tipo_veiculo_id(id, tipo_veiculo)')
        .in('pedido_id', allTargetIds);

      if (error) {
        console.warn('[getUnlinkedOSForMedicaoCliente] Supabase error:', error.message);
        throw error;
      }

      const list = osData || [];

      // 3. Filter in memory by date range, allowed statuses (4: Finalizada, 6: Aguardando Assinatura), and unlinked status
      const eligibleOS = list.filter(os => {
        const sId = Number(os.status_id);

        // Check if already linked to a different medicao (ignoring dummy medicao 1)
        const linkedMedId = os.medicoes_id ? Number(os.medicoes_id) : null;
        const isLinkedToCurrent = Boolean(currentMedicaoId && linkedMedId === Number(currentMedicaoId));

        if (linkedMedId && linkedMedId > 1 && !isLinkedToCurrent) {
          return false; // already linked to another valid medicao
        }

        // Permite apenas ordens de serviço Finalizadas (4) ou Aguardando Assinatura (6),
        // ou ordens que já pertencem à própria medição em edição
        const isAllowedStatus = sId === 4 || sId === 6 || isLinkedToCurrent;
        if (!isAllowedStatus) {
          return false;
        }

        // Date check
        if (!os.data_atendimento) return false;
        const osDateStr = String(os.data_atendimento).split('T')[0].split(' ')[0];
        if (osDateStr < startDateStr || osDateStr > endDateStr) {
          return false;
        }

        return true;
      });

      const total = eligibleOS.reduce((sum, item) => sum + (Number(item.total_os) || 0), 0);
      return {
        ordens: eligibleOS,
        total,
        count: eligibleOS.length
      };
    } catch (err: any) {
      console.error('Error fetching unlinked OS for medicao cliente:', err);
      throw err;
    }
  },

  async getUnlinkedOSForMedicaoAgregado(agregadoId: number, inicio: string, fim: string, currentMedicaoId?: number): Promise<{ ordens: any[], total: number, count: number }> {
    try {
      if (!agregadoId || !inicio || !fim) {
        return { ordens: [], total: 0, count: 0 };
      }

      const startDateStr = inicio.split('T')[0];
      const endDateStr = fim.split('T')[0];

      // O PostgREST não aceita filtrar a OS por coluna de tabela embutida dentro
      // de .or() ("veiculos.motorista_id") — a consulta falhava com
      // "invalid column for filter" e o fallback só trazia as OS por `agregado`,
      // perdendo as feitas com o veículo do motorista. Agora são duas consultas
      // válidas, mescladas por id.
      const selectOs = '*, pedido:pedido_id(pessoa_id, pessoa:pessoa_id(id, nome)), veiculos:veiculos_id(placa, veiculo_modelo:veiculo_modelo_id(id, modelo), motorista:motorista_id(nome)), item_servico_rel:item_servico(id, servicos, tipo_servico:tipo_servico_id(id, tipo)), tipo_veiculo:tipo_veiculo_id(id, tipo_veiculo)';

      const { data: veiculosDoMotorista } = await supabase
        .from('veiculos')
        .select('id')
        .eq('motorista_id', agregadoId);
      const veiculoIds = (veiculosDoMotorista || []).map((v: any) => v.id);

      const [porAgregado, porVeiculo]: any[] = await Promise.all([
        supabase.from('ordem_de_servico').select(selectOs).eq('agregado', agregadoId),
        veiculoIds.length > 0
          ? supabase.from('ordem_de_servico').select(selectOs).in('veiculos_id', veiculoIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (porAgregado.error) console.warn('Erro ao buscar OS por agregado:', porAgregado.error.message);
      if (porVeiculo.error) console.warn('Erro ao buscar OS por veículo do motorista:', porVeiculo.error.message);

      const osPorId = new Map<number, any>();
      [...(porAgregado.data || []), ...(porVeiculo.data || [])].forEach((os: any) => osPorId.set(Number(os.id), os));
      const list = Array.from(osPorId.values());

      const eligibleOS = list.filter(os => {
        const sId = Number(os.status_id);

        const linkedAgregadoMedId = os.medicoes_agregado ? Number(os.medicoes_agregado) : null;
        const isLinkedToCurrent = Boolean(currentMedicaoId && linkedAgregadoMedId === Number(currentMedicaoId));

        if (linkedAgregadoMedId && linkedAgregadoMedId > 0 && !isLinkedToCurrent) {
          return false;
        }

        // Permite ordens de serviço Finalizadas (4), Aguardando Assinatura (6), Medição Cliente Gerada (7) ou Faturada (9),
        // ou ordens que já pertencem à própria medição em edição.
        // Como a medição de agregado não altera o status da OS, a OS pode já ter status 7 ou 9 por causa da medição de cliente.
        const isAllowedStatus = sId === 4 || sId === 6 || sId === 7 || sId === 9 || isLinkedToCurrent;
        if (!isAllowedStatus) {
          return false;
        }

        if (!os.data_atendimento) return false;
        const osDateStr = String(os.data_atendimento).split('T')[0].split(' ')[0];
        if (osDateStr < startDateStr || osDateStr > endDateStr) {
          return false;
        }

        return true;
      });

      const total = eligibleOS.reduce((sum, item) => sum + (Number(item.valor_total_repasses) || 0), 0);
      return {
        ordens: eligibleOS,
        total,
        count: eligibleOS.length
      };
    } catch (err: any) {
      console.error('Error fetching unlinked OS for medicao agregado:', err);
      throw err;
    }
  },

  async calculateMedicao(clienteId: number, inicio: string, fim: string) {
    const result = await this.getUnlinkedOSForMedicaoCliente(clienteId, inicio, fim);
    return { total: result.total, count: result.count };
  },

  async calculateMedicaoAgregado(agregadoId: number, inicio: string, fim: string) {
    const result = await this.getUnlinkedOSForMedicaoAgregado(agregadoId, inicio, fim);
    return { total: result.total, count: result.count };
  },

  async getOSByMedicaoAgregado(medicaoId: number) {
    try {
      const { data, error } = await supabase
        .from('ordem_de_servico')
        .select('*, pedido:pedido_id(pessoa:pessoa_id(id, nome)), veiculos:veiculos_id(placa, veiculo_modelo:veiculo_modelo_id(id, modelo), motorista:motorista_id(nome)), item_servico_rel:item_servico(id, servicos, tipo_servico:tipo_servico_id(id, tipo)), tipo_veiculo:tipo_veiculo_id(id, tipo_veiculo)')
        .eq('medicoes_agregado', medicaoId)
        .order('id', { ascending: false });

      if (error) throw error;
      let list = data || [];

      // 1. Resolve Agregado directly from medicao_agregado header
      const { data: medAgregado } = await supabase
        .from('medicoes_agregado')
        .select('agregado_id, agregado:agregado_id(id, nome)')
        .eq('id', medicaoId)
        .single();

      const defaultAgregadoNome = (medAgregado as any)?.agregado?.nome;

      // 2. Resolve Agregados by IDs present in the OS list (os.agregado)
      const agregadoIds = Array.from(new Set(list.map(os => os.agregado).filter(Boolean))) as number[];
      const pMap = new Map<number, string>();
      if (agregadoIds.length > 0) {
        const { data: pessoas } = await supabase.from('pessoa').select('id, nome').in('id', agregadoIds);
        if (pessoas) {
          pessoas.forEach(p => pMap.set(p.id, p.nome.trim()));
        }
      }

      return list.map(os => {
        const nome = (os.agregado && pMap.get(os.agregado)) ||
          defaultAgregadoNome ||
          os.veiculos?.motorista?.nome ||
          '-';
        return {
          ...os,
          agregado_nome: nome
        };
      });
    } catch (err) {
      console.error('Error fetching OS by medicao agregado:', err);
      return [];
    }
  },

  async getOSByMedicaoCliente(medicaoId: number) {
    try {
      // 1. Fetch OS directly linked by medicoes_id
      const { data: directOS, error: directErr } = await supabase
        .from('ordem_de_servico')
        .select('*, pedido:pedido_id(pessoa_id, pessoa:pessoa_id(id, nome)), veiculos:veiculos_id(placa, veiculo_modelo:veiculo_modelo_id(id, modelo), motorista:motorista_id(nome)), item_servico_rel:item_servico(id, servicos, tipo_servico:tipo_servico_id(id, tipo)), tipo_veiculo:tipo_veiculo_id(id, tipo_veiculo)')
        .eq('medicoes_id', medicaoId)
        .order('id', { ascending: false });

      if (directErr) throw directErr;
      let osList = directOS || [];

      // 2. Fetch OS linked via medicoes_detalhes
      const { data: detList } = await supabase
        .from('medicoes_detalhes')
        .select('ordem_de_servico_id')
        .eq('medicao_id', medicaoId);
        
      const detOsIds = Array.from(new Set(
        (detList || []).map(d => d.ordem_de_servico_id).filter(Boolean)
      )) as number[];
      
      if (detOsIds.length > 0) {
        const existingIds = new Set(osList.map(os => os.id));
        const newOsIds = detOsIds.filter(id => !existingIds.has(id));
        
        if (newOsIds.length > 0) {
          const { data: indirectOS, error: indirectErr } = await supabase
            .from('ordem_de_servico')
            .select('*, pedido:pedido_id(pessoa_id, pessoa:pessoa_id(id, nome)), veiculos:veiculos_id(placa, veiculo_modelo:veiculo_modelo_id(id, modelo), motorista:motorista_id(nome)), item_servico_rel:item_servico(id, servicos, tipo_servico:tipo_servico_id(id, tipo)), tipo_veiculo:tipo_veiculo_id(id, tipo_veiculo)')
            .in('id', newOsIds)
            .order('id', { ascending: false });
            
          if (!indirectErr && indirectOS) {
            osList = [...osList, ...indirectOS];
          }
        }
      }

      // Check if any OS are missing client information and resolve manually
      const missingClientPedidoIds = osList
        .filter(os => {
          const rawP = (os as any).pedido?.pessoa;
          const p = Array.isArray(rawP) ? rawP[0] : rawP;
          return !p?.nome && os.pedido_id;
        })
        .map(os => os.pedido_id);

      if (missingClientPedidoIds.length > 0) {
        const { data: pedidos } = await supabase
          .from('pedido')
          .select('id, pessoa_id, pessoa:pessoa_id(id, nome)')
          .in('id', missingClientPedidoIds);

        if (pedidos && pedidos.length > 0) {
          const pedMap = new Map<number, any>(pedidos.map(p => [p.id, p]));
          osList = osList.map(os => {
            const rawP = (os as any).pedido?.pessoa;
            const p = Array.isArray(rawP) ? rawP[0] : rawP;
            if (!p?.nome && os.pedido_id && pedMap.has(os.pedido_id)) {
              return {
                ...os,
                pedido: pedMap.get(os.pedido_id)
              };
            }
            return os;
          });
        }
      }

      return osList;
    } catch (err) {
      console.error('Error fetching OS by medicao cliente:', err);
      return [];
    }
  },

  async removeOSFromMedicaoAgregado(osId: number, medicaoId?: number) {
    // 1. Descobre medicaoId se não passado
    let targetMedicaoId = medicaoId;
    if (!targetMedicaoId) {
      const { data: osData } = await supabase
        .from('ordem_de_servico')
        .select('medicoes_agregado')
        .eq('id', osId)
        .single();
      targetMedicaoId = osData?.medicoes_agregado ? Number(osData.medicoes_agregado) : undefined;
    }

    // 2. Desvincula a OS sem alterar o status da OS (medições de agregado não alteram o status)
    const { error } = await supabase
      .from('ordem_de_servico')
      .update({ medicoes_agregado: null })
      .eq('id', osId);
    if (error) throw error;

    // 3. Recalcula o valor total restante da medição de agregado
    if (targetMedicaoId) {
      const { data: remainingOS } = await supabase
        .from('ordem_de_servico')
        .select('valor_total_repasses')
        .eq('medicoes_agregado', targetMedicaoId);

      const newTotal = (remainingOS || []).reduce(
        (sum, item) => sum + (Number(item.valor_total_repasses) || 0),
        0
      );

      await supabase
        .from('medicoes_agregado')
        .update({ valor_medicao: newTotal })
        .eq('id', targetMedicaoId);
    }
  },

  async removeOSFromMedicaoCliente(osId: number, medicaoId?: number) {
    // 1. Descobre medicaoId se não passado
    let targetMedicaoId = medicaoId;
    if (!targetMedicaoId) {
      const { data: osData } = await supabase
        .from('ordem_de_servico')
        .select('medicoes_id')
        .eq('id', osId)
        .single();
      targetMedicaoId = osData?.medicoes_id ? Number(osData.medicoes_id) : undefined;
    }

    // 2. Remove de medicoes_detalhes se existir
    await supabase
      .from('medicoes_detalhes')
      .delete()
      .eq('ordem_de_servico_id', osId);

    // 3. Desvincula a OS e redefine status para 4 (Finalizada)
    const { error } = await supabase
      .from('ordem_de_servico')
      .update({ medicoes_id: null, status_id: 4 })
      .eq('id', osId);
    if (error) throw error;

    // 4. Recalcula o valor total restante da medição de cliente
    if (targetMedicaoId) {
      const { data: remainingOS } = await supabase
        .from('ordem_de_servico')
        .select('total_os')
        .eq('medicoes_id', targetMedicaoId);

      const newTotal = (remainingOS || []).reduce(
        (sum, item) => sum + (Number(item.total_os) || 0),
        0
      );

      await supabase
        .from('medicoes')
        .update({ valor_medicao: newTotal })
        .eq('id', targetMedicaoId);
    }
  },

  async getStatusMedicao(): Promise<StatusMedicao[]> {
    const defaultStatuses: StatusMedicao[] = [
      { id: 1, nome: 'Em Análise', cor: '#d97706' },
      { id: 2, nome: 'Aprovado', cor: '#16a34a' },
      { id: 3, nome: 'Reprovado', cor: '#dc2626' },
      { id: 4, nome: 'Faturado', cor: '#2563eb' }
    ];

    try {
      const { data, error } = await supabase.from('status_medicao').select('*');
      if (error || !data || data.length === 0) {
        return defaultStatuses;
      }
      
      return data.map(s => ({
        ...s,
        nome: cleanStatusMedicaoName(s.nome),
        cor: s.cor || (s.nome?.toLowerCase().includes('an') ? '#d97706' : s.nome?.toLowerCase().includes('aprov') ? '#16a34a' : s.nome?.toLowerCase().includes('reprov') ? '#dc2626' : '#2563eb')
      }));
    } catch (e) {
      return defaultStatuses;
    }
  },

  async createMedicao(medicao: Omit<Medicoes, 'id'>): Promise<Medicoes> {
    const { data, error } = await supabase
      .from('medicoes')
      .insert([medicao])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async createMedicaoAgregado(medicao: Omit<MedicoesAgregado, 'id'>): Promise<MedicoesAgregado> {
    const { data, error } = await supabase
      .from('medicoes_agregado')
      .insert([medicao])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteMedicao(id: number): Promise<void> {
    // 1. Remove details
    await supabase.from('medicoes_detalhes').delete().eq('medicao_id', id);
    
    // 2. Unlink OS and reset status to 4 (FINALIZADO)
    await supabase
      .from('ordem_de_servico')
      .update({ medicoes_id: null, status_id: 4 })
      .eq('medicoes_id', id);

    // 3. Delete medicao
    const { error } = await supabase.from('medicoes').delete().eq('id', id);
    if (error) throw error;
  },

  async deleteMedicaoAgregado(id: number): Promise<void> {
    // 1. Unlink OS sem alterar status (medições de agregado não alteram status da OS)
    await supabase
      .from('ordem_de_servico')
      .update({ medicoes_agregado: null })
      .eq('medicoes_agregado', id);

    // 2. Delete medicao agregado
    const { error } = await supabase.from('medicoes_agregado').delete().eq('id', id);
    if (error) throw error;
  }
};
